import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Checkbox, Hourglass, NumberInput, Panel, Slider, Text, TextInput } from 'react95-native-rabbl';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller, Resolver } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Gender } from '@/types/user_info';
import {useWindowDimensions} from 'react-native';
import { Meta } from '@/components/Meta';
import { AppText } from '@/components/AppText';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  panelStyle: {
    minWidth: 400,
  },
  sliderStyle: {
    width: '100%',
  },
});

export default function Height() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const {height : screenHeight, width : screenWidth} = useWindowDimensions();
  const router = useRouter();
  const [height, setHeight] = useState<number>(
    communityMembership && communityMembership.user_info && communityMembership.user_info.gender == Gender.GENDER_MAN ?
        AppConsts.AVERAGE_HEIGHT_MAN
      :
        AppConsts.AVERAGE_HEIGHT_WOMAN
  );
  const dispatch = useDispatch();

  const sliderWidth = Math.min(Math.floor(screenWidth * 0.8), 500);

  const onSubmit = useCallback(async() => {
    try {
      showSpinner();

      if (height < AppConsts.MIN_HEIGHT_INCHES || height > AppConsts.MAX_HEIGHT_INCHES) {
        throw new Error('Invalid height');
      }

      const response = await AppUtilities.fetchAuthorized(
        "Height.onSubmit",
        "/rabbl/api/set_height",
        "POST",
        {
          domain: domain,
          height: height,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "Height.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain, height]);

  return (
    <View style={AppConstants.STYLES.basicViewCenter}>
      <Meta title={"Your height"} />
            
      <Panel variant='raised' elevation={4} style={{ width: sliderWidth + 20 }}>
        <View style={styles.panel}>

          <AppText>What's your height?</AppText>

          <CustomText>{AppUtils.getHeightHumanReadable(height)}</CustomText>

          <Slider
            min={AppConsts.MIN_HEIGHT_INCHES}
            max={AppConsts.MAX_HEIGHT_INCHES}
            onChange={(v) => setHeight(v)}
            onChangeCommitted={(v) => {}}
            step={1}
            value={height}
            style={{ width: sliderWidth }}
            showToolTip={false}
          />

          <View style={styles.buttonWrapper}>
            <Button95
              onPress={onSubmit}
              disabled={height == AppConsts.MIN_HEIGHT_INCHES}
            >Next</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </View>
  );
}
